// Phone-number normalizer — the canonical source of truth for every phone
// comparison / save in the web app.
//
// Mirrors Dispatch-Mobile-App/services/phone.ts EXACTLY so web and mobile
// agree on what two phone numbers mean. Twilio expects E.164
// (+1XXXXXXXXXX) for SMS / voice — anything else gets rejected or
// silently routed to the wrong number.
//
// Policy:
//   • Write path → always convert to E.164 (+1XXXXXXXXXX) before saving.
//   • Read path → when comparing, pipe both sides through `phonesMatch`
//     so data saved before this policy still compares correctly.
//   • Display path → `formatForDisplay` for human-readable rendering.
//
// Covers the formats people type into phone fields: +1, 1-, bare 10
// digits, parens, dashes, dots, spaces, leading country code, etc.

/** Strip to digits and drop a leading NA '1'. Returns 10-digit bare, or
 *  raw digits if it's not a recognisable North American number. */
export function normalizePhone(raw: string | null | undefined): string {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.substring(1);
  }
  return digits;
}

/**
 * Key a Text Inbox thread for read-marks and unread counts. Phone senders key
 * by normalized digits; ALPHANUMERIC senders ("test", carrier names) have no
 * digits at all, and keying them by normalizePhone() collapsed every one of
 * them onto '' — and markTextRead bailed on the empty key, so their unread
 * chip could never clear. They key by their trimmed lowercased name instead.
 */
export function textThreadKey(raw: string | null | undefined): string {
  return normalizePhone(raw) || String(raw ?? '').trim().toLowerCase();
}

/** Return E.164 form (+1XXXXXXXXXX). If the input isn't a valid NA
 *  number, returns the raw input unchanged so we don't corrupt
 *  international or partially-typed numbers.  Pair with isValidPhone()
 *  at form-submit time to reject unsavable values. */
export function toE164(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = normalizePhone(raw);
  if (isNaPhoneDigits(digits)) return `+1${digits}`;
  // Not a clean 10-digit NA number. Preserve raw input so the caller can
  // decide whether to reject.
  return String(raw).trim();
}

/** True if `raw` is a recognisable 10-digit North American phone. */
export function isValidPhone(raw: string | null | undefined): boolean {
  return isNaPhoneDigits(normalizePhone(raw));
}

/** NANP shape: area code and exchange each start with 2–9. A 10-digit
 *  string like "1793612345" is not a phone (mirrors normalize-phone.js). */
export function isNaPhoneDigits(digits: string): boolean {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
}

/** Compare two phone strings tolerantly — old un-normalized data
 *  compares equal to new E.164 data as long as the 10 digits match. */
export function phonesMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const na = normalizePhone(a);
  const nb = normalizePhone(b);
  if (!na || !nb) return false;
  return na === nb;
}

/** Render a phone as `(807) 707-5733` if it's a 10-digit NA number;
 *  otherwise return the input trimmed. Use for customer-facing labels
 *  (order detail sheet, admin team list). Never use for comparisons. */
export function formatForDisplay(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = normalizePhone(raw);
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return String(raw).trim();
}
